import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';

import {
  Cell,
  Label,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';

import { config } from '../../api/config';
import {
  buildDevicesPresetFromIds,
  FAULTY_CHART_SEGMENT_NAME,
} from '../../pages/monitoring/devices/devices.helpers';

import styles from './deviceStatistics.module.scss';

const DeviceStatistics = ({
  title,
  subTitle,
  chartData,
  chartLabel,
  faultyDeviceIds,
}) => {
  const history = useHistory();
  const hasFaultyDevices = Boolean(faultyDeviceIds?.length);

  const handlePieClick = useCallback(
    segment => {
      if (segment?.name !== FAULTY_CHART_SEGMENT_NAME || !hasFaultyDevices) {
        return;
      }

      const presetDeviceFilters = buildDevicesPresetFromIds(faultyDeviceIds);

      if (!presetDeviceFilters) {
        return;
      }

      history.push({
        pathname: `${config.APP_PATHS.monitoring}/devices`,
        state: { presetDeviceFilters },
      });
    },
    [faultyDeviceIds, hasFaultyDevices, history]
  );

  return (
    <div className={styles.deviceStatisticsContainer}>
      <div>
        <h2 className={styles.chartCardTitle}>{title}</h2>
        <span className={styles.chartCardSubtitle}>{subTitle}</span>
      </div>
      <ResponsiveContainer width="100%" height={180}>
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            startAngle={-270}
            innerRadius={55}
            outerRadius={70}
            paddingAngle={0}
            dataKey="value"
            onClick={handlePieClick}
          >
            {chartData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.fill}
                cursor={
                  entry.name === FAULTY_CHART_SEGMENT_NAME && hasFaultyDevices
                    ? 'pointer'
                    : 'default'
                }
              />
            ))}
            {chartLabel && (
              <Label
                width={100}
                position="center"
                fill="var(--admin-text-secondary, #75757d)"
              >
                {chartLabel}
              </Label>
            )}
          </Pie>
          <Tooltip formatter={value => `${value}%`} />
          <Legend iconType="circle" />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

DeviceStatistics.propTypes = {
  title: PropTypes.string.isRequired,
  subTitle: PropTypes.string.isRequired,
  chartLabel: PropTypes.string,
  faultyDeviceIds: PropTypes.arrayOf(PropTypes.number),
  chartData: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      fill: PropTypes.string.isRequired,
    })
  ),
};

export default DeviceStatistics;
